import React, { useState } from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface DateRangePickerProps {
  value: { start: Date | undefined; end: Date | undefined };
  onChange: (range: { start: Date | undefined; end: Date | undefined }) => void;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  minDate,
  maxDate = new Date(),
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectingStart, setSelectingStart] = useState(true);

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) return;

    if (selectingStart) {
      // Selecting start date
      onChange({ start: date, end: undefined });
      setSelectingStart(false);
    } else {
      // Selecting end date
      if (value.start && date < value.start) {
        // If end date is before start date, swap them
        onChange({ start: date, end: value.start });
      } else {
        onChange({ start: value.start, end: date });
      }
      setIsOpen(false);
      setSelectingStart(true);
    }
  };

  const handleClear = () => {
    onChange({ start: undefined, end: undefined });
    setSelectingStart(true);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSelectingStart(true);
    }
  };

  const formatDateRange = () => {
    if (!value.start && !value.end) {
      return 'Select date range';
    }
    if (value.start && !value.end) {
      return `${format(value.start, 'MMM d, yyyy')} - Select end date`;
    }
    if (value.start && value.end) {
      return `${format(value.start, 'MMM d, yyyy')} - ${format(value.end, 'MMM d, yyyy')}`;
    }
    return 'Select date range';
  };

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'w-full justify-start text-left font-normal',
              !value.start && !value.end && 'text-muted-foreground'
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {formatDateRange()}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="p-3 border-b">
            <p className="text-sm font-medium">
              {selectingStart ? 'Select start date' : 'Select end date'}
            </p>
            {value.start && !value.end && (
              <p className="text-xs text-muted-foreground mt-1">
                Start: {format(value.start, 'MMM d, yyyy')}
              </p>
            )}
          </div>
          <Calendar
            mode="single"
            selected={selectingStart ? value.start : value.end}
            onSelect={handleDateSelect}
            disabled={(date) => {
              if (minDate && date < minDate) return true;
              if (maxDate && date > maxDate) return true;
              if (!selectingStart && value.start && date < value.start) return true;
              return false;
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {(value.start || value.end) && (
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={handleClear}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Clear date range</span>
        </Button>
      )}
    </div>
  );
};
